import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';

import { filter, map, take } from 'rxjs/operators';

import { APP_PATHS_SLASH } from '@app/core/enums/paths/app.paths';
import type { UserRole } from '@interfaces/settings.interface';
import { AuthStore } from '@store/auth.store';

/**
 * Enforces a route's `data.roles` against the current user's role, waiting for the host resource
 * to resolve or fail first so a cold deep-link decides on the real role rather than a transient
 * null. Routes without role restrictions remain readable; restricted routes deny access
 * without a session, a valid role, or when loading the host fails.
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const allowedRoles = route.data['roles'] as readonly UserRole[] | undefined;
  if (!allowedRoles?.length) {
    return true;
  }

  const decide = (): boolean => {
    const role = authStore.currentUserRole();
    if (authStore.session() && !authStore.hostError() && role && allowedRoles.includes(role)) {
      return true;
    }
    void router.navigate([APP_PATHS_SLASH.HOME]);
    return false;
  };

  const hostSettled = computed(() => !!authStore.host() || !!authStore.hostError());

  if (!authStore.session() || hostSettled()) {
    return decide();
  }

  return toObservable(hostSettled).pipe(
    filter(settled => settled),
    take(1),
    map(decide)
  );
};
